import {useTranslation} from '@aboutyou/i18n';
import {styled} from '@linaria/react';
import React, {FC, useState} from 'react';
import {COLORS} from '../../../constants/colors';
import {Button} from '../../atoms/Buttons/Button';
import {AnimatedWishButton, useAnimatedWishButton} from './AnimatedWishButton';

export type CTACircleWishButtonConfigProps = {
  variant: 'filled' | 'not-filled' | 'not-filled-with-outline';
  withBackground?: boolean;
  isFocusable?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  className?: string;
  style?: React.CSSProperties;
  'aria-label'?: string;
  round?: boolean;
  'data-testid'?: string;
};

export const CTAWishButton: FC<CTACircleWishButtonConfigProps> = ({
  variant,
  onClick,
  withBackground,
  'aria-label': ariaLabel,
  isFocusable,
  round,
  'data-testid': dataTestId,
}) => {
  const {t} = useTranslation();
  const [hovered, setIsHovered] = useState(false);
  const {hasClicked, resetClick, handleHasClicked} = useAnimatedWishButton();

  const handleOnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    handleHasClicked();
  };

  const ariaLabelToUse =
    ariaLabel ||
    (variant === 'filled'
      ? t('Item in whishlist')
      : t('Add item to whishlist'));

  return (
    <StyledButton
      onClick={handleOnClick}
      aria-label={ariaLabelToUse}
      size="sm"
      round={round}
      variant="secondary"
      disableTabNavigation={!isFocusable}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-shape={round ? 'circle' : 'square'}
      data-testid={dataTestId}
    >
      <AnimatedWishButton
        variant={variant}
        hovered={hovered}
        hasClicked={hasClicked}
        resetClick={resetClick}
        withBackground={withBackground}
      />
    </StyledButton>
  );
};

const StyledButton = styled(Button)`
  &[data-shape='circle'] {
    position: relative;
    margin-top: 16px;
    z-index: 999;
  }

  &[data-shape='square'] {
    margin-left: 10px;
    border: solid 1px ${COLORS['Outline.Grey']};
    max-width: 48px;
    width: 100%;

    && {
      border-radius: 4px;
      box-shadow: none;
    }
  }
`;
